import { Form, Input, notification, Select, Space } from "antd";
import { ReactNode, useContext, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { BoardGameSearchEntry, UserBoardGameEntry } from "../api.types";
import { CurrentUserContext, useHttp } from "../Context";
import { HttpFetch } from "../fetch";

interface SearchOption {
    label: string,
    value: number,
    game: BoardGameSearchEntry|UserBoardGameEntry
}

export function GameSelect(props: { value: BoardGameSearchEntry, note?: string, onChange: (e?: BoardGameSearchEntry) => any, onNoteChange?: (note: string) => any }) {
    const {currentUser} = useContext(CurrentUserContext)
    const [options, setOptions] = useState({ term: "", values: [] as SearchOption[], loading: false });
    const httpFetch: HttpFetch = useHttp()

    const serverSearch = useDebouncedCallback((value: string) => {
        setOptions({ values: options.values, term: value, loading: true })
        if (!value) {
            return
        }
        const params = new URLSearchParams([["search", value]])
        const url = "/api/games?"+params.toString()
        httpFetch(url).then((resp) => resp.json().then((json) => {return {data: json, raw: resp}})).then(response => {
            const r = response.data.reduce((prev: SearchOption[], curr: BoardGameSearchEntry): SearchOption[] => {
                if (curr.name) {
                    prev.push({ label: curr.name + (curr.publicationYear ? ` (${curr.publicationYear})` : ""), value: curr.id, game: curr })
                }
                return prev
            }, [])
            setOptions((prev) => {
                if(prev.term !== value) {
                    return prev
                }
                return {
                    loading: false,
                    term: value,
                    values: r
                }
            })
            if (response.raw.headers.has('X-Partial-Result')) {
                notification.warning({message: "Częściowe wyniki", duration: 3})
            }
        })
    }, 500, { maxWait: 1000 })
    const onSearch = (value: string) => {
        if (value) {
            serverSearch(value)
        } else {
            setOptions({ term: "", loading: false, values: [] })
        }
    }
    const onChange = (v: any, o: any) => {
        if (props.onChange && o?.game) {
            props.onChange(o.game)
        }
        if (props.onNoteChange) {
            props.onNoteChange(o?.game?.note || "")
        }
    }
    const onClear = () => {
        if (props.onChange) {
            props.onChange(undefined)
        }
        if (props.onNoteChange) {
            props.onNoteChange("")
        }
    }
    const onNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.onNoteChange) {
            props.onNoteChange(e.target.value)
        }
    }

    return (
        <Form.Item>
            <Space.Compact>
                <Form.Item noStyle>
                    <Select
                        style={{ width: 300 }}
                        filterOption={true}
                        optionFilterProp="label"
                        allowClear={true}
                        showSearch={true}
                        options={prepareOptions(options.values, currentUser?.games || [], options.term || "")}
                        loading={options.loading}
                        onSearch={onSearch}
                        onChange={onChange}
                        onClear={onClear}
                        optionRender={optionRender}
                        value={props.value ? gameEntryToOption(props.value) : undefined}
                    />
                </Form.Item>
                {
                    props.onNoteChange ?
                    <Form.Item noStyle>
                        <Input style={{ width: '35%' }} placeholder="notatka" value={props.note} onChange={onNoteChange}/>
                    </Form.Item>
                    : undefined
                }
            </Space.Compact>
        </Form.Item>
    );
}

function gameEntryToOption(curr: BoardGameSearchEntry): SearchOption {
    return { label: curr.name + (curr.publicationYear ? ` (${curr.publicationYear})` : ""), value: curr.id, game: curr }
}

function prepareOptions(opts: SearchOption[], userGames: UserBoardGameEntry[], term: string): SearchOption[] {
    const lowerCaseTerm = term.toLocaleLowerCase()
    const userOpts = userGames.filter((val: UserBoardGameEntry) => {
        return term === "" || val.name.toLocaleLowerCase().indexOf(lowerCaseTerm) !== -1
    })
    .reduce((prev: SearchOption[], curr: UserBoardGameEntry): SearchOption[] => {
        if (curr.name) {
            prev.push(gameEntryToOption(curr))
        }
        return prev
    }, [])
    return userOpts.concat(opts.filter((el) => {
        return userOpts.map((e: SearchOption) => e.value).indexOf(el.value) === -1
    }))
    .sort((a: SearchOption, b: SearchOption) => {
        const aStartsWithTerm = a.game.name.toLocaleLowerCase().indexOf(lowerCaseTerm) === 0
        const bStartsWithTerm =b.game.name.toLocaleLowerCase().indexOf(lowerCaseTerm) === 0
        if (aStartsWithTerm === bStartsWithTerm){
            return a.game.name.localeCompare(b.game.name)
        } else if (aStartsWithTerm) {
            return -1
        } else {
            return 1
        }
    })
}

function optionRender(option: any): ReactNode {
    return (
        <div style={{ display: "flex", alignItems: "center" }}>
            {option?.data?.game?.thumbnail ? <div style={{ width: 32, height: 32, flexShrink: 0, justifyContent: "center", display: "flex" }}><img src={option.data.game.thumbnail} style={{ maxWidth: 32, maxHeight: 32, margin: "auto" }} alt="" /></div> : null}
            <div style={{ display: "inline-flex" }}>&nbsp;{option.label}</div>
        </div>
    )
}
